import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Flex, Text, Badge, HStack, Input, InputGroup, InputLeftElement, Select, Button, VStack, Container, SimpleGrid, Heading,
} from '@chakra-ui/react';
import { FiSearch, FiMapPin, FiClock, FiBriefcase, FiArrowRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { careerApi } from './careerApi';

const JOB_TYPE_LABEL = { full_time: 'Full-time', part_time: 'Part-time', contract: 'Contract', internship: 'Internship' };

export default function JobsList() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [jobType, setJobType] = useState('');
  const [team, setTeam] = useState('');

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function load() {
    setLoading(true);
    try {
      const res = await careerApi.jobs();
      setJobs(res?.jobs || []);
    } catch (_) {}
    setLoading(false);
  }

  const teams = useMemo(() => Array.from(new Set(jobs.map(j => j.Team?.name).filter(Boolean))).sort(), [jobs]);

  const filtered = useMemo(() => {
    return jobs.filter(j => {
      if (jobType && j.job_type !== jobType) return false;
      if (team && j.Team?.name !== team) return false;
      if (search) {
        const q = search.toLowerCase();
        return (j.title || '').toLowerCase().includes(q)
            || (j.JobRole?.name || '').toLowerCase().includes(q)
            || (j.Team?.name || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [jobs, search, jobType, team]);

  return (
    <Box>
      {/* Hero */}
      <Box
        bgGradient="linear(to-br, #E0F2FF 0%, #F0F9FF 40%, #F7F9FC 100%)"
        borderBottom="1px solid #E5E9F0"
        pt={{ base: '48px', md: '80px' }}
        pb={{ base: '56px', md: '96px' }}>
        <Container maxW="1200px">
          <Box maxW="720px">
            <Text fontSize="12px" color="#1E76CE" fontWeight="800" letterSpacing="2px" textTransform="uppercase" mb="12px">
              We're hiring
            </Text>
            <Heading as="h1" fontSize={{ base: '32px', md: '48px' }} fontWeight="800" color="#0F172A" lineHeight="1.15" mb="16px">
              Build the internet of the <Text as="span" color="#1E76CE">next billion</Text> with us.
            </Heading>
            <Text fontSize={{ base: '15px', md: '18px' }} color="#475569" lineHeight="1.55" mb="28px">
              At eOcean we build the messaging, payments and engagement rails that power some of the largest apps in the region.
              Come solve problems at scale with a team that ships every week.
            </Text>
            <HStack spacing="12px" wrap="wrap">
              <Box bg="white" borderRadius="14px" boxShadow="0 4px 16px rgba(30,118,206,0.08)" p="8px" display="inline-flex" alignItems="center" w={{ base: '100%', md: 'auto' }}>
                <InputGroup size="md" w={{ base: '100%', md: '340px' }}>
                  <InputLeftElement pointerEvents="none" pl="4px"><FiSearch color="#94A3B8" /></InputLeftElement>
                  <Input placeholder="Search by role, team, or keyword" border="none" _focus={{ boxShadow: 'none' }} value={search} onChange={e => setSearch(e.target.value)} />
                </InputGroup>
              </Box>
            </HStack>
          </Box>
        </Container>
      </Box>

      {/* Filters + Grid */}
      <Container maxW="1200px" py="40px">
        <Flex justifyContent="space-between" alignItems="center" mb="20px" flexWrap="wrap" gap="12px">
          <HStack spacing="8px" wrap="wrap">
            <FilterChip active={jobType === ''} onClick={() => setJobType('')}>All types</FilterChip>
            <FilterChip active={jobType === 'full_time'} onClick={() => setJobType('full_time')}>Full-time</FilterChip>
            <FilterChip active={jobType === 'part_time'} onClick={() => setJobType('part_time')}>Part-time</FilterChip>
            <FilterChip active={jobType === 'contract'} onClick={() => setJobType('contract')}>Contract</FilterChip>
            <FilterChip active={jobType === 'internship'} onClick={() => setJobType('internship')}>Internship</FilterChip>
          </HStack>
          <HStack spacing="10px">
            {teams.length > 0 && (
              <Select size="sm" w="200px" borderRadius="10px" bg="white" value={team} onChange={e => setTeam(e.target.value)}>
                <option value="">All teams</option>
                {teams.map(t => <option key={t} value={t}>{t}</option>)}
              </Select>
            )}
            <Text fontSize="13px" color="#64748B" fontWeight="600">{filtered.length} open</Text>
          </HStack>
        </Flex>

        {loading && <SkeletonList />}

        {!loading && filtered.length === 0 && (
          <Box bg="white" border="1px solid #E5E9F0" borderRadius="16px" p="80px 24px" textAlign="center">
            <Text fontSize="18px" color="#334155" fontWeight="700" mb="6px">No open positions match your filters</Text>
            <Text fontSize="13px" color="#94A3B8">Try removing a filter or check back soon — we're growing fast.</Text>
          </Box>
        )}

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="16px">
          {filtered.map(j => (
            <JobCard key={j.id} job={j} />
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <Button size="sm" onClick={onClick} borderRadius="full" px="16px" fontSize="13px" fontWeight="600"
      bg={active ? '#1E76CE' : 'white'} color={active ? 'white' : '#334155'}
      border="1px solid" borderColor={active ? '#1E76CE' : '#E5E9F0'}
      _hover={{ bg: active ? '#1A67B4' : '#F8FAFC' }}>
      {children}
    </Button>
  );
}

function JobCard({ job }) {
  return (
    <Box as={Link} to={`/jobs/${job.req_no}`}
      bg="white" border="1px solid #E5E9F0" borderRadius="16px" p="24px 26px"
      transition="all 0.15s"
      _hover={{ borderColor: '#1E76CE', boxShadow: '0 8px 24px rgba(30,118,206,0.08)', transform: 'translateY(-2px)' }}>
      <Flex justifyContent="space-between" alignItems="flex-start" mb="10px">
        <Badge bg="#E0F2FF" color="#1E76CE" fontSize="10px" px="10px" py="4px" borderRadius="full" textTransform="uppercase" letterSpacing="0.5px" fontWeight="700">
          {JOB_TYPE_LABEL[job.job_type] || job.job_type}
        </Badge>
        <FiArrowRight color="#94A3B8" />
      </Flex>
      <Text fontSize="19px" fontWeight="700" color="#0F172A" mb="6px" lineHeight="1.3" noOfLines={2}>
        {job.title || job.JobRole?.name}
      </Text>
      <Text fontSize="13px" color="#64748B" mb="16px" noOfLines={2}>
        {stripHtml(job.job_description_html) || `${job.Designation?.name || ''} · ${job.JobRole?.name || ''}`}
      </Text>
      <HStack spacing="14px" fontSize="12px" color="#64748B" flexWrap="wrap">
        {job.Team?.name && <HStack spacing="4px"><FiBriefcase /><Text>{job.Team.name}</Text></HStack>}
        {job.Location?.name && <HStack spacing="4px"><FiMapPin /><Text>{job.Location.name}</Text></HStack>}
        {job.Shift?.name && <HStack spacing="4px"><FiClock /><Text>{job.Shift.name}</Text></HStack>}
        {job.experience_min != null && <Text color="#94A3B8">· {job.experience_min}–{job.experience_max ?? '?'} yrs</Text>}
      </HStack>
    </Box>
  );
}

function SkeletonList() {
  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="16px">
      {[1, 2, 3, 4].map(i => (
        <Box key={i} bg="white" border="1px solid #E5E9F0" borderRadius="16px" p="24px 26px" h="200px">
          <Box bg="#F1F5F9" h="14px" w="80px" borderRadius="4px" mb="16px" />
          <Box bg="#F1F5F9" h="20px" w="70%" borderRadius="4px" mb="10px" />
          <Box bg="#F1F5F9" h="14px" w="90%" borderRadius="4px" mb="6px" />
          <Box bg="#F1F5F9" h="14px" w="60%" borderRadius="4px" />
        </Box>
      ))}
    </SimpleGrid>
  );
}

function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}
